import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { CardRowTitle } from 'components/CardRowTitle';
import { ViewWithSeparators } from 'components/ViewWithSeparators';
import { useEsc } from 'hooks/useEsc';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { ScreenSize } from 'lib/constants';
import { theme } from 'lib/theme';
import React from 'react';
import { useDispatch } from 'react-redux';
import {
  doAseShowValidationErrors,
  NAVIGATE_MODAL_BACK,
  SET_GENERIC,
} from 'store/actions';
import {
  selectAseIsEditable,
  selectAseExplorerOptions,
  selectAseExplorerOptionShowValidationErrors,
} from 'store/assessments/selectors';
import { ExplorerOptionsProps as TExplorerOptions } from 'store/assessments/types';
import { GENERIC_ASSESSMENT_EXPLORER_OPTIONS } from 'types/types';

export const ExplorerOptions: React.FC = () => {
  const dispatch = useDispatch();

  const screenSize = useScreenSize();
  const isEditable = useSelector(selectAseIsEditable);
  const explorerOptions = useSelector(selectAseExplorerOptions);
  const showValidationErrors = useSelector(
    selectAseExplorerOptionShowValidationErrors,
  );

  const handleSetExplorerOptions = (
    explorerOptionChanges: Partial<TExplorerOptions>,
  ) =>
    dispatch(
      SET_GENERIC({
        key: GENERIC_ASSESSMENT_EXPLORER_OPTIONS,
        value: { ...explorerOptions, ...explorerOptionChanges },
      }),
    );

  const handleSetShowValidationErrors = (newValue: boolean) => {
    if (!newValue) {
      dispatch(doAseShowValidationErrors(newValue));
    }
    handleSetExplorerOptions({
      showValidationErrors: newValue,
    });
  };

  const sizeDescription =
    explorerOptions.size === 'normal'
      ? 'Normal text size and spacing between items'
      : 'Fit more on a page with smaller text and less spacing';

  useEsc(() => dispatch(NAVIGATE_MODAL_BACK()));

  return (
    <>
      <Card label="List Size" explanation={sizeDescription}>
        <ViewWithSeparators horizontal>
          <CardRow
            style={{ flex: 1 }}
            label="Normal"
            radioValue={explorerOptions.size === 'normal'}
            onPress={() =>
              handleSetExplorerOptions({
                size: 'normal',
              })
            }
          />
          <CardRow
            style={{ flex: 1 }}
            label="Small"
            radioValue={explorerOptions.size === 'small'}
            onPress={() =>
              handleSetExplorerOptions({
                size: 'small',
              })
            }
          />
        </ViewWithSeparators>
      </Card>
      <Card label="List Details" marginBottomQuarter>
        <CardRowTitle label="Questions & Checklists" />
        <ViewWithSeparators horizontal>
          <CardRow
            style={{ flex: 1 }}
            label="Short Description"
            labelAlignTop
            radioValue={explorerOptions.questionDescriptions === 'short'}
            onPress={() =>
              handleSetExplorerOptions({
                questionDescriptions: 'short',
              })
            }
            compact
          />
          <CardRow
            style={{ flex: 1 }}
            label="Full Description"
            labelAlignTop
            radioValue={explorerOptions.questionDescriptions === 'full'}
            onPress={() =>
              handleSetExplorerOptions({
                questionDescriptions: 'full',
              })
            }
            compact
          />
        </ViewWithSeparators>
      </Card>
      <Card marginBottomQuarter>
        <CardRowTitle label="Actions & Controls" />
        <ViewWithSeparators horizontal>
          <CardRow
            style={{ flex: 1 }}
            label="Short Description"
            labelAlignTop
            radioValue={explorerOptions.findingDescriptions === 'short'}
            onPress={() =>
              handleSetExplorerOptions({
                findingDescriptions: 'short',
              })
            }
            compact
          />
          <CardRow
            style={{ flex: 1 }}
            label="Full Description"
            labelAlignTop
            radioValue={explorerOptions.findingDescriptions === 'full'}
            onPress={() =>
              handleSetExplorerOptions({
                findingDescriptions: 'full',
              })
            }
            compact
          />
        </ViewWithSeparators>
        <CardRow
          style={{ flex: 1 }}
          label="Show Categories, Severities, Timescales,"
          checkedValue={explorerOptions.findingAdditional}
          onPress={() =>
            handleSetExplorerOptions({
              findingAdditional: !explorerOptions.findingAdditional,
            })
          }
          compact
        />
      </Card>
      <Card>
        <CardRowTitle label="Action & Control Suggestions" />
        <ViewWithSeparators horizontal>
          <CardRow
            style={{ flex: 1 }}
            label="Short Description"
            labelAlignTop
            radioValue={
              explorerOptions.findingSuggestionDescriptions === 'short'
            }
            onPress={() =>
              handleSetExplorerOptions({
                findingSuggestionDescriptions: 'short',
              })
            }
            compact
          />
          <CardRow
            style={{ flex: 1 }}
            label="Full Description"
            labelAlignTop
            radioValue={
              explorerOptions.findingSuggestionDescriptions === 'full'
            }
            onPress={() =>
              handleSetExplorerOptions({
                findingSuggestionDescriptions: 'full',
              })
            }
            compact
          />
        </ViewWithSeparators>
        <CardRow
          style={{ flex: 1 }}
          label="Show Categories, Severities, Timescales, etc"
          checkedValue={explorerOptions.findingSuggestionAdditional}
          onPress={() =>
            handleSetExplorerOptions({
              findingSuggestionAdditional:
                !explorerOptions.findingSuggestionAdditional,
            })
          }
        />
      </Card>
      <Card label="Questions & Checklists">
        {isEditable && (
          <CardRow
            label="Show Unanswered"
            checkedValue={explorerOptions.questionsUnanswered}
            onPress={() =>
              handleSetExplorerOptions({
                questionsUnanswered: !explorerOptions.questionsUnanswered,
              })
            }
            showBadge={!explorerOptions.questionsUnanswered}
          />
        )}
        <CardRow
          label="Show Negatively Answered"
          checkedValue={explorerOptions.questionsNegative}
          onPress={() =>
            handleSetExplorerOptions({
              questionsNegative: !explorerOptions.questionsNegative,
            })
          }
          showBadge={!explorerOptions.questionsNegative}
        />
        <CardRow
          label="Show Positively Answered"
          checkedValue={explorerOptions.questionsPositive}
          onPress={() =>
            handleSetExplorerOptions({
              questionsPositive: !explorerOptions.questionsPositive,
            })
          }
          showBadge={!explorerOptions.questionsPositive}
        />
      </Card>
      {screenSize !== ScreenSize.small && (
        <Card
          label="Actions & Controls"
          style={{ marginBottom: theme.paddingVertical / 4 }}
        >
          <CardRow
            label="Hide Actions & Controls"
            value="They'll be shown when you select a question"
            labelAlignTop
            checkedValue={!!explorerOptions.findingsCollapse}
            onPress={() =>
              handleSetExplorerOptions({
                findingsCollapse: !explorerOptions.findingsCollapse,
              })
            }
          />
        </Card>
      )}
      <Card
        label={
          screenSize !== ScreenSize.small ? undefined : 'Actions & Controls'
        }
        style={{ marginBottom: theme.paddingVertical / 4 }}
      >
        <CardRow
          label="Show References"
          value="Show action & control references"
          labelAlignTop
          checkedValue={!!explorerOptions.findingShowReference}
          onPress={() =>
            handleSetExplorerOptions({
              findingShowReference: !explorerOptions.findingShowReference,
            })
          }
        />
      </Card>
      {isEditable ? (
        <Card>
          <CardRow
            label="Show New & Reviewed"
            value="Actions & controls added to this assessment"
            labelAlignTop
            checkedValue={explorerOptions.findingsNewReviewed}
            onPress={() =>
              handleSetExplorerOptions({
                findingsNewReviewed: !explorerOptions.findingsNewReviewed,
              })
            }
            showBadge={!explorerOptions.findingsNewReviewed}
          />
          <CardRow
            label="Show Unreviewed"
            value="Actions & controls from previous assessments but not added to this assessment"
            labelAlignTop
            checkedValue={explorerOptions.findingsUnreviewed}
            onPress={() =>
              handleSetExplorerOptions({
                findingsUnreviewed: !explorerOptions.findingsUnreviewed,
              })
            }
            showBadge={!explorerOptions.findingsUnreviewed}
          />
        </Card>
      ) : (
        <Card>
          <CardRow
            label="Show Actions & Controls"
            checkedValue
            onPress={() =>
              handleSetExplorerOptions({
                findingsNewReviewed: !explorerOptions.findingsNewReviewed,
              })
            }
            showBadge={!explorerOptions.findingsNewReviewed}
          />
        </Card>
      )}

      {isEditable && (
        <Card label="Validation">
          <CardRow
            onPress={() => handleSetShowValidationErrors(!showValidationErrors)}
            label="Show Validation Errors"
            value="Highlight incomplete fields, etc"
            labelAlignTop
            checkedValue={showValidationErrors}
          />
        </Card>
      )}
    </>
  );
};
